// src/pages/Users/Galleries/GalleryCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { deleteGallery } from "../../../services/gallerieServices";
import EditAlbumModal from "./EditAlbumModal";
import { toast } from "react-toastify";
import { FaPlay, FaPause, FaEye, FaPen, FaTrash } from "react-icons/fa";

// ⬇️ helper pour /uploads/...
import { toAbsolutePhotoUrl } from "../../../services/albumPhotoServices";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const FALLBACK = "/img/no-image.jpg";

function stripHtml(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

function onImgError(e) {
  if (e?.currentTarget) e.currentTarget.src = FALLBACK;
}

export default function GalleryCard({
  gallery,
  index,
  userRole,
  refreshGalleriesList,
}) {
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false); // ▶️ pas d’autoplay par défaut
  const [expanded, setExpanded] = useState(false);

  // Sanitize HTML (DOMPurify import dynamique)
  const [purify, setPurify] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {
        // pas grave : fallback non-sanitized (recommandé: npm i dompurify)
      }
    })();
  }, []);

  const isManager = userRole === "admin" || userRole === "enseignant";
  const photos = Array.isArray(gallery.photos) ? gallery.photos : [];
  const hasMany = photos.length > 1;

  const cover = toAbsolutePhotoUrl(photos?.[0]?.photo_url || FALLBACK);

  // Texte brut pour estimer la longueur (détermine si on affiche "Voir plus")
  const descText = useMemo(
    () => stripHtml(gallery.description || ""),
    [gallery.description]
  );
  const shouldClamp = descText.length > 220;

  // HTML sécurisé pour l’affichage enrichi
  const safeHtml = useMemo(() => {
    const raw = gallery.description || "";
    try {
      return purify ? purify.sanitize(raw) : raw;
    } catch {
      return raw;
    }
  }, [gallery.description, purify]);

  const settings = {
    dots: true,
    infinite: photos.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true, // ⚠️ utilisé uniquement si isPlaying === true (voir rendu conditionnel)
    autoplaySpeed: 3000,
    arrows: false,
    lazyLoad: "ondemand",
    pauseOnHover: true,
    adaptiveHeight: false,
  };

  const handleViewAlbumClick = () =>
    navigate(`/galerie-photos/album/${gallery.id}`);

  const handleDeleteGallery = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette galerie ?"))
      return;
    try {
      await deleteGallery(gallery.id);
      toast.success("Galerie supprimée avec succès.");
      refreshGalleriesList?.();
    } catch (error) {
      console.error("Erreur lors de la suppression de la galerie:", error);
      toast.error("Erreur lors de la suppression de la galerie.");
    }
  };

  return (
    <article className="rounded-2xl overflow-hidden bg-white/80 dark:bg-slate-900/60 backdrop-blur shadow ring-1 ring-slate-200/70 dark:ring-slate-800/70 flex flex-col h-full">
      {/* Media */}
      <div className="relative">
        {/* Image statique par défaut (pas d’autoplay) */}
        {!isPlaying || !hasMany ? (
          <div
            className="w-full bg-slate-50 dark:bg-slate-900"
            style={{ aspectRatio: "16 / 9" }}
          >
            <img
              src={cover}
              alt={gallery.titre}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={onImgError}
            />
          </div>
        ) : (
          // Slider rendu uniquement quand l’utilisateur lance le défilement
          <Slider {...settings}>
            {photos.map((p, i) => (
              <div
                key={`${gallery.id}-p-${i}`}
                className="w-full bg-slate-50 dark:bg-slate-900"
                style={{ aspectRatio: "16 / 9" }}
              >
                <img
                  src={toAbsolutePhotoUrl(p.photo_url) || FALLBACK}
                  alt={`${gallery.titre} - photo ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={onImgError}
                />
              </div>
            ))}
          </Slider>
        )}

        {/* Badges overlay (compteur + statut) */}
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-white/80 dark:bg-slate-900/70 ring-1 ring-slate-200 dark:ring-slate-700 text-slate-700 dark:text-slate-200">
            {photos.length} photo{photos.length > 1 ? "s" : ""}
          </span>
          {!gallery.is_active && (
            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-amber-100/80 dark:bg-amber-900/40 ring-1 ring-amber-200 dark:ring-amber-800 text-amber-700 dark:text-amber-300">
              Inactif
            </span>
          )}
        </div>

        {/* Bouton Play/Pause discret (en haut à droite) */}
        {hasMany && (
          <button
            type="button"
            onClick={() => setIsPlaying((v) => !v)}
            title={
              isPlaying ? "Mettre en pause le diaporama" : "Lancer le diaporama"
            }
            aria-label={isPlaying ? "Pause" : "Lecture"}
            className="absolute right-3 top-3 w-9 h-9 grid place-items-center rounded-full
                       bg-white/80 dark:bg-slate-900/70 ring-1 ring-slate-200 dark:ring-slate-700
                       text-slate-700 dark:text-slate-200 hover:ring-sky-300 transition"
          >
            {isPlaying ? (
              <FaPause className="text-sm" />
            ) : (
              <FaPlay className="text-sm" />
            )}
          </button>
        )}
      </div>

      {/* Body : titre + description HTML (sous l’image) */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-slate-900 dark:text-white text-base sm:text-lg font-semibold">
          {gallery.titre}
        </h3>

        {safeHtml ? (
          <div className="relative">
            <div
              className={`prose prose-sm dark:prose-invert max-w-none
                [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-sky-700 dark:[&_a]:text-sky-400
                ${expanded ? "" : "max-h-24 overflow-hidden"}`}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
            {!expanded && shouldClamp && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white dark:from-slate-900 to-transparent" />
            )}
            {shouldClamp && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 inline-flex items-center text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white focus:outline-none focus:underline"
              >
                {expanded ? "Réduire" : "Voir plus"}
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs italic text-slate-500 dark:text-slate-400">
            Pas de description.
          </p>
        )}

        {/* Actions fines */}
        <div className="mt-2 flex items-center justify-between">
          {/* Lien “Voir l’album” discret */}
          <button
            type="button"
            onClick={handleViewAlbumClick}
            className="inline-flex items-center gap-2 text-sm font-semibold ring-2
                       text-sky-700 hover:text-sky-900 dark:text-sky-400 dark:hover:text-sky-300
                       focus:outline-none focus:ring-2 focus:ring-violet-400 rounded-lg px-1.5 py-1"
            title="Ouvrir l’album"
          >
            <FaEye /> Voir l’album
          </button>

          {/* Icônes admin/enseignant : fines */}
          {isManager && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditModalOpen(true)}
                title="Éditer"
                aria-label="Éditer"
                className="w-9 h-9 grid place-items-center rounded-lg ring-1 ring-slate-200 dark:ring-slate-700
                           text-slate-600 dark:text-slate-300 hover:ring-sky-300 hover:text-slate-900
                           focus:outline-none focus:ring-2 focus:ring-violet-400 transition"
              >
                <FaPen className="text-sm" />
              </button>
              <button
                type="button"
                onClick={handleDeleteGallery}
                title="Supprimer"
                aria-label="Supprimer"
                className="w-9 h-9 grid place-items-center rounded-lg ring-1 ring-slate-200 dark:ring-slate-700
                           text-red-600 hover:ring-red-300 hover:text-red-700
                           focus:outline-none focus:ring-2 focus:ring-red-400 transition"
              >
                <FaTrash className="text-sm" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modale édition */}
      {editModalOpen && (
        <EditAlbumModal
          gallery={gallery}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          refreshList={refreshGalleriesList}
        />
      )}
    </article>
  );
}
